import { FormEvent, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

type RequestPurpose =
  | 'investor'
  | 'hospital'
  | 'clinic'
  | 'government'
  | 'strategic_partner'
  | 'research'
  | 'other';

interface FormState {
  name: string;
  organization: string;
  role: string;
  email: string;
  phone: string;
  purpose: RequestPurpose;
  message: string;
  consent: boolean;
}

const initialFormState: FormState = {
  name: '',
  organization: '',
  role: '',
  email: '',
  phone: '',
  purpose: 'strategic_partner',
  message: '',
  consent: false,
};

const purposeOptions: Array<{ value: RequestPurpose; label: string }> = [
  { value: 'investor', label: 'Investor / Fund' },
  { value: 'hospital', label: 'Hospital Group' },
  { value: 'clinic', label: 'Clinic / Medical Practice' },
  { value: 'government', label: 'Government / Public Health Body' },
  { value: 'strategic_partner', label: 'Strategic Partner' },
  { value: 'research', label: 'Research / Academic Institution' },
  { value: 'other', label: 'Other' },
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ConfidentialAccessSection() {
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validationError = useMemo(() => {
    if (!form.name.trim()) return 'Full name is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!emailPattern.test(form.email.trim())) return 'Please provide a valid email address.';
    if (!form.consent) return 'Please confirm consent before submitting.';
    return null;
  }, [form]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validationError) {
      toast({
        title: 'Request not submitted',
        description: validationError,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-contact-request', {
        body: {
          name: form.name,
          organization: form.organization,
          role: form.role,
          email: form.email,
          phone: form.phone,
          purpose: form.purpose,
          message: form.message,
          consent: form.consent,
        },
      });

      if (error || !data?.ok) {
        throw new Error(data?.error || error?.message || 'Unable to submit request.');
      }

      setForm(initialFormState);
      toast({
        title: 'Request received',
        description: 'Our team will review your request and contact you if access is approved.',
      });
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : 'Unable to submit request at this time.';
      const isEdgeGatewayFailure =
        rawMessage.includes('Failed to send a request to the Edge Function') ||
        rawMessage.includes('FunctionsFetchError') ||
        rawMessage.includes('TypeError: Failed to fetch');

      toast({
        title: 'Submission failed',
        description: isEdgeGatewayFailure
          ? 'Contact service is temporarily unavailable. Please try again shortly.'
          : rawMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="confidential-access" className="border-t border-border/30 py-16 md:py-20">
      <div className="container mx-auto max-w-[1400px] px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid items-start gap-5 xl:gap-7 lg:grid-cols-[1.04fr_1fr]"
        >
          <div className="self-start rounded-lg border border-border/30 bg-card/40 p-6 md:p-8">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border/40 px-3 py-1 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" />
              Restricted Partner Material
            </div>
            <h3 className="mb-3 text-2xl md:text-[2.05rem] font-semibold leading-tight text-foreground">
              Confidential Operational Blueprint
            </h3>
            <p className="mb-5 text-base leading-[1.65] text-muted-foreground">
              Detailed operational architecture is available to verified partners upon request.
              Access is controlled, reviewed manually, and granted only when aligned with clinical
              safety, regulatory, and strategic collaboration requirements.
            </p>
            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Non-Sensitive Preview
            </div>
            <div className="rounded-md border border-primary/35 bg-background/85 p-5 md:p-6 text-base md:text-lg leading-[1.6] text-foreground">
              This public page provides only a non-sensitive preview. Full documentation is restricted to authorized internal and approved partner stakeholders.
            </div>

            <div className="mt-6 space-y-2.5">
              <div className="h-2.5 w-[88%] rounded-sm bg-muted/50" />
              <div className="h-2.5 w-[72%] rounded-sm bg-muted/40" />
              <div className="h-2.5 w-[84%] rounded-sm bg-muted/35" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rounded-lg border border-border/30 bg-card/60 p-6 md:p-7">
            <div className="mb-5 flex items-center gap-2 text-sm font-medium text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Request Access
            </div>

            <div className="grid gap-3.5">
              <Input
                required
                aria-label="Full Name"
                placeholder="Full Name *"
                value={form.name}
                maxLength={120}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <Input
                aria-label="Organization or Company"
                placeholder="Organization / Company"
                value={form.organization}
                maxLength={160}
                onChange={(e) => setForm((prev) => ({ ...prev, organization: e.target.value }))}
              />
              <Input
                aria-label="Role or Title"
                placeholder="Role / Title"
                value={form.role}
                maxLength={120}
                onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
              />
              <Input
                required
                type="email"
                aria-label="Email"
                placeholder="Email *"
                value={form.email}
                maxLength={254}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              />
              <Input
                type="tel"
                aria-label="Phone"
                placeholder="Phone (optional)"
                value={form.phone}
                maxLength={30}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              />

              <Select
                value={form.purpose}
                onValueChange={(value: RequestPurpose) => setForm((prev) => ({ ...prev, purpose: value }))}
              >
                <SelectTrigger aria-label="Purpose of request">
                  <SelectValue placeholder="Purpose of request" />
                </SelectTrigger>
                <SelectContent>
                  {purposeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea
                aria-label="Message and additional details"
                placeholder="Message / Additional details"
                value={form.message}
                maxLength={2000}
                className="min-h-36"
                onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              />

              <label className="mt-1 flex items-start gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(checked) =>
                    setForm((prev) => ({ ...prev, consent: checked === true }))
                  }
                />
                <span>
                  I confirm the submitted details are accurate and consent to being contacted regarding
                  this access request.
                </span>
              </label>
            </div>

            <div className="mt-5 flex items-center justify-between gap-3">
              <p className="text-[11px] text-muted-foreground">
                Manual review only. No automated distribution.
              </p>
              <Button type="submit" disabled={isSubmitting || Boolean(validationError)}>
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
